import { useEffect, useState } from 'react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { TextInputField } from '@shared/ui/forms/FormFields';
import { Button } from '@shared/ui/primitives/Button';
import { useMemberProfile } from '@shared/hooks/useMemberProfile';
import type { EventSummary } from './api';
import { approvalGuestBadgeClass, formatApprovalStatus } from './eventRsvpUi';

export type GuestDraft = { name: string; email: string };

type Step = 'ask' | 'guests' | 'status';

interface GuestRsvpSheetProps {
  open: boolean;
  event: EventSummary | null;
  mode?: 'rsvp' | 'view';
  submitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (guests: GuestDraft[]) => void;
}

function emptyGuest(): GuestDraft {
  return { name: '', email: '' };
}

function memberDisplayName(profile?: {
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
}): string {
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  return fullName || profile?.name?.trim() || profile?.email?.trim() || 'You';
}

function RsvpParticipantRow({
  role,
  name,
  email,
  approvalStatus,
}: {
  role: string;
  name: string;
  email?: string | null;
  approvalStatus?: string | null;
}): JSX.Element {
  return (
    <li className="rounded-md bg-sunken px-3 py-2 text-body-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-caption font-medium uppercase tracking-[0.08em] text-ink-muted">{role}</p>
          <p className="font-medium text-ink">{name}</p>
          {email ? <p className="text-ink-secondary">{email}</p> : null}
        </div>
        {approvalStatus ? (
          <span className={approvalGuestBadgeClass(approvalStatus)}>
            {formatApprovalStatus(approvalStatus)}
          </span>
        ) : null}
      </div>
    </li>
  );
}

export function GuestRsvpSheet({
  open,
  event,
  mode = 'rsvp',
  submitting = false,
  onOpenChange,
  onSubmit,
}: GuestRsvpSheetProps): JSX.Element {
  const [step, setStep] = useState<Step>('ask');
  const [guests, setGuests] = useState<GuestDraft[]>([emptyGuest()]);
  const profileQuery = useMemberProfile();
  const maxGuests = event?.max_guests ?? 1;

  useEffect(() => {
    if (!open) return;
    setStep(mode === 'view' ? 'status' : 'ask');
    setGuests([emptyGuest()]);
  }, [open, mode, event?.event_id]);

  const handleOpenChange = (next: boolean): void => {
    onOpenChange(next);
  };

  const updateGuest = (index: number, patch: Partial<GuestDraft>): void => {
    setGuests((current) => current.map((guest, i) => (i === index ? { ...guest, ...patch } : guest)));
  };

  const addGuest = (): void => {
    if (guests.length >= maxGuests) return;
    setGuests((current) => [...current, emptyGuest()]);
  };

  const removeGuest = (index: number): void => {
    setGuests((current) => (current.length <= 1 ? current : current.filter((_, i) => i !== index)));
  };

  const submitGuests = (): void => {
    const validGuests = guests
      .map((guest) => ({ name: guest.name.trim(), email: guest.email.trim() }))
      .filter((guest) => guest.name && guest.email);
    if (validGuests.length === 0) return;
    onSubmit(validGuests);
  };

  const title =
    mode === 'view'
      ? 'RSVP details'
      : step === 'ask'
        ? 'Add guests?'
        : step === 'guests'
          ? 'Guest details'
          : 'Your RSVP';

  const profile = profileQuery.data;
  const memberName = memberDisplayName(profile);
  const memberEmail = profile?.email;

  return (
    <BottomSheet open={open} onOpenChange={handleOpenChange} title={title}>
      {!event ? null : step === 'ask' ? (
        <div className="flex flex-col gap-4 pb-6">
          <p className="text-body-sm text-ink-secondary">
            You can bring guests to this event. An admin will review your RSVP before it is confirmed.
          </p>
          <Button variant="primary" width="full" disabled={submitting} onClick={() => onSubmit([])}>
            No, just me
          </Button>
          <Button variant="secondary" width="full" disabled={submitting} onClick={() => setStep('guests')}>
            Yes, add guests
          </Button>
        </div>
      ) : step === 'guests' ? (
        <div className="flex flex-col gap-4 pb-6">
          {guests.map((guest, index) => (
            <div key={index} className="space-y-3 rounded-lg border border-hairline p-3">
              <TextInputField
                label="Name"
                required
                value={guest.name}
                onChange={(e) => updateGuest(index, { name: e.target.value })}
                placeholder="Guest name"
                autoCapitalize="words"
              />
              <TextInputField
                label="Email"
                type="email"
                required
                value={guest.email}
                onChange={(e) => updateGuest(index, { email: e.target.value })}
                placeholder="guest@email.com"
                autoComplete="email"
                inputMode="email"
              />
              {guests.length > 1 ? (
                <Button variant="ghost" width="full" onClick={() => removeGuest(index)}>
                  Remove guest
                </Button>
              ) : null}
            </div>
          ))}
          {guests.length < maxGuests ? (
            <Button variant="secondary" width="full" onClick={addGuest}>
              + Add guest
            </Button>
          ) : null}
          <Button variant="primary" width="full" disabled={submitting} onClick={submitGuests}>
            Submit RSVP
          </Button>
          <Button variant="ghost" width="full" onClick={() => setStep('ask')}>
            Back
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 pb-6">
          <p className="text-body-sm text-ink-secondary">
            Your registration status and any guests included with your RSVP.
          </p>
          <ul className="space-y-2">
            <RsvpParticipantRow
              role="You"
              name={memberName}
              email={memberEmail}
              approvalStatus={event.approval_status}
            />
            {(event.rsvp_guests ?? []).map((guest) => (
              <RsvpParticipantRow
                key={`${guest.guest_email}-${guest.guest_name}`}
                role="Guest"
                name={guest.guest_name?.trim() || guest.guest_email?.trim() || 'Guest'}
                email={guest.guest_email}
                approvalStatus={guest.approval_status}
              />
            ))}
          </ul>
          <Button variant="primary" width="full" onClick={() => handleOpenChange(false)}>
            Done
          </Button>
        </div>
      )}
    </BottomSheet>
  );
}
